import {
  default as React,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import { useCurrentUserEmail } from '../microapps/hooks';
import {
  AccessPickerGroupHeader,
  AccessPickerOption,
  useAccessPickerStyles,
  type AccessKind,
} from '../../../scaffolder/accessPickerUi';

// Dropdown sections — same labels as the deploy form's Individual Users picker.
const INTERNAL_GROUP = 'WellSky Users';
const EXTERNAL_GROUP = 'External Users';
const INVITE_GROUP = 'Invite by Email';
const isInviteOption = (opt: { displayName: string }) =>
  opt.displayName.startsWith('Add "');

// ── Annotation keys ────────────────────────────────────────────────────────────

const ANNOTATION_OWNER_EMAILS = 'microapp/owners';
const ANNOTATION_CIAM_GROUP_ID = 'skystage.io/ciam-group-id';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Interfaces ─────────────────────────────────────────────────────────────────

interface UserOption {
  email: string;
  displayName: string;
  source: 'internal' | 'external';
}

interface CiamGroup {
  id: string;
  name: string;
  description: string;
}

const INTERNAL_CHIP_SX = {
  height: '28px',
  fontSize: '0.85rem',
  fontWeight: 500,
  backgroundColor: '#c8e6c9',
  color: '#1b5e20',
  border: '1px solid #a5d6a7',
} as const;

const EXTERNAL_CHIP_SX = {
  height: '28px',
  fontSize: '0.85rem',
  fontWeight: 500,
  backgroundColor: '#e1bee7',
  color: '#4a148c',
  border: '1px solid #ce93d8',
} as const;

const GROUP_CHIP_SX = {
  height: '28px',
  fontSize: '0.85rem',
  fontWeight: 500,
  backgroundColor: '#bbdefb',
  color: '#0d47a1',
  border: '1px solid #90caf9',
} as const;

// ── Helpers ────────────────────────────────────────────────────────────────────

function getUserChipTitle(
  isOwner: boolean,
  isPending: boolean,
  isInternalUser: boolean,
): string {
  if (isOwner) return 'Owner — cannot be removed';
  if (isPending) return 'Pending addition';
  if (isInternalUser) return 'WellSky user — SSO login, no invite sent';
  return 'External user — invite sent if new to WellSky Identity';
}

// ── PublicAccessTab ────────────────────────────────────────────────────────────

/**
 * Entity page tab for managing access to a public-hosted app via WellSky Identity (CIAM).
 *
 * Owners can:
 *  - Add/remove individual users (internal @wellsky.com via catalog, or external via CIAM)
 *  - Add/remove CIAM client groups (all group members get access)
 *  - Submit batch changes — CIAM group membership is synced on Save.
 */
export const PublicAccessTab = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const currentUserEmail = useCurrentUserEmail();
  // IDP-1304: same dropdown styling as the deploy form's access pickers.
  const pickerClasses = useAccessPickerStyles();

  const appName = entity.metadata.name;
  const annotations = useMemo(
    () => entity.metadata.annotations ?? {},
    [entity.metadata.annotations],
  );

  const ownerEmails = useMemo(() => {
    const raw = annotations[ANNOTATION_OWNER_EMAILS] ?? '';
    return raw
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(Boolean);
  }, [annotations]);

  const ciamGroupId = annotations[ANNOTATION_CIAM_GROUP_ID] ?? '';

  const isOwner = useMemo(
    () =>
      currentUserEmail
        ? ownerEmails.includes(currentUserEmail.toLowerCase())
        : false,
    [ownerEmails, currentUserEmail],
  );

  const backendUrl = config.getString('backend.baseUrl');
  const accessBase = `${backendUrl}/api/microapps-api/ciam/apps/${appName}`;

  // ── State ──────────────────────────────────────────────────────────────────

  /** Individual users currently in the CIAM group (loaded from backend). */
  const [loadedUsers, setLoadedUsers] = useState<string[]>([]);
  const [pendingUsers, setPendingUsers] = useState<string[]>([]);

  /** Client group names — stored in annotation, managed locally. */
  const annotationGroupNames = useMemo(() => {
    const raw = annotations['skystage.io/allowed-group-names'] ?? '';
    return raw
      .split(',')
      .map(g => g.trim())
      .filter(Boolean);
  }, [annotations]);

  // Saved group list. Starts from the annotation and is updated after a
  // successful save, so a second save before the catalog refreshes does not
  // re-send the same group additions/removals.
  const [loadedGroupNames, setLoadedGroupNames] =
    useState<string[]>(annotationGroupNames);
  useEffect(() => {
    setLoadedGroupNames(annotationGroupNames);
  }, [annotationGroupNames]);

  const [pendingGroups, setPendingGroups] =
    useState<string[]>(annotationGroupNames);

  const [displayNames, setDisplayNames] = useState<Map<string, string>>(
    new Map(),
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Autocomplete state — users
  const [userInput, setUserInput] = useState('');
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [userSearching, setUserSearching] = useState(false);
  const userTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didInitialLoadRef = useRef(false);

  // Autocomplete state — groups
  const [groupInput, setGroupInput] = useState('');
  const [groupOptions, setGroupOptions] = useState<CiamGroup[]>([]);
  const [groupSearching, setGroupSearching] = useState(false);
  const groupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Maps group name → member emails fetched when the group was added. Used to
   *  clean up group-derived emails from the visible chip list and to restore
   *  them when a group is removed. */
  const [groupMembersCache, setGroupMembersCache] = useState<
    Map<string, string[]>
  >(new Map());

  /**
   * Emails that are in pendingUsers (individual grant kept in DB) but are
   * hidden from the chip display because they are already covered by one of
   * the current client groups.  Kept separate so:
   *   - The individual access row is preserved in the DB (survives group remove)
   *   - No duplicate chip shown (user sees group chip, not an extra individual chip)
   */
  const [hiddenByGroupEmails, setHiddenByGroupEmails] = useState<Set<string>>(
    new Set(),
  );

  // ── Helpers ────────────────────────────────────────────────────────────────

  const getAuthHeaders = useCallback(async (): Promise<HeadersInit> => {
    const { token } = await identityApi.getCredentials();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [identityApi]);

  const hasChanges = useMemo(
    () =>
      JSON.stringify([...pendingUsers].sort()) !==
        JSON.stringify([...loadedUsers].sort()) ||
      JSON.stringify([...pendingGroups].sort()) !==
        JSON.stringify([...loadedGroupNames].sort()),
    [pendingUsers, loadedUsers, pendingGroups, loadedGroupNames],
  );

  // ── Load current CIAM group members ───────────────────────────────────────

  const loadAccess = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const headers = await getAuthHeaders();
      // Pass current client groups so the backend can subtract group-derived
      // members and return only explicitly added individual users.
      const groupsParam = annotationGroupNames.length
        ? `?groups=${encodeURIComponent(annotationGroupNames.join(','))}`
        : '';
      const res = await fetch(`${accessBase}/access${groupsParam}`, {
        headers,
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const bodyError = (body as { error?: unknown }).error;
        throw new Error(
          typeof bodyError === 'string'
            ? bodyError
            : `Failed to load access list (${res.status})`,
        );
      }
      const data = (await res.json()) as {
        users: string[];
        individualUsers?: string[];
        allowedGroups?: string[];
      };
      // Use individualUsers when available (backend filters out group-derived
      // members). Fall back to users for older backend versions.
      let emails: string[];
      if (Array.isArray(data.individualUsers)) {
        emails = data.individualUsers;
      } else if (Array.isArray(data.users)) {
        emails = data.users;
      } else {
        emails = [];
      }
      setLoadedUsers(emails);
      setPendingUsers(emails);
      // Use allowedGroups from backend (populated from the in-process cache
      // rather than the catalog annotation) so groups are correct immediately
      // after a save, even before the catalog re-indexes the argo-repo file.
      if (Array.isArray(data.allowedGroups) && data.allowedGroups.length > 0) {
        setLoadedGroupNames(data.allowedGroups);
        setPendingGroups(data.allowedGroups);

        // Pre-populate groupMembersCache for already-loaded groups so that
        // handleRemoveGroup can remove their members from pendingUsers even
        // when the group was never freshly added in this session.
        const { token } = await identityApi.getCredentials();
        const authHeaders: Record<string, string> = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        const groupMemberEntries = await Promise.all(
          data.allowedGroups.map(async (groupName: string) => {
            try {
              // Resolve the group's Okta ID first via the search endpoint, then
              // fetch members. We search by name because the DB only stores names.
              const searchRes = await fetch(
                `${backendUrl}/api/microapps-api/ciam/groups/search`,
                {
                  method: 'POST',
                  headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json',
                  },
                  credentials: 'include',
                  body: JSON.stringify({ search: groupName, pageSize: 5 }),
                },
              );
              if (!searchRes.ok) return { groupName, members: [] as string[] };
              const searchData = (await searchRes.json()) as {
                items?: Array<{ id: string; name: string }>;
              };
              const match = (searchData.items ?? []).find(
                g => g.name === groupName,
              );
              if (!match) return { groupName, members: [] as string[] };

              const membersRes = await fetch(
                `${backendUrl}/api/microapps-api/ciam/groups/${match.id}/members`,
                { headers: authHeaders, credentials: 'include' },
              );
              if (!membersRes.ok) return { groupName, members: [] as string[] };
              const membersData = (await membersRes.json()) as {
                emails: string[];
              };
              return { groupName, members: membersData.emails ?? [] };
            } catch {
              return { groupName, members: [] as string[] };
            }
          }),
        );
        // Build the full set of group-derived emails across all loaded groups.
        const allGroupEmails = new Set<string>();
        const cacheUpdates = new Map<string, string[]>();
        for (const { groupName, members } of groupMemberEntries) {
          if (members.length > 0) {
            cacheUpdates.set(groupName, members);
            members.forEach(e => allGroupEmails.add(e.toLowerCase()));
          }
        }
        if (cacheUpdates.size > 0) {
          setGroupMembersCache(prev => {
            const next = new Map(prev);
            cacheUpdates.forEach((members, groupName) =>
              next.set(groupName, members),
            );
            return next;
          });
        }
        // Users who are both individually added (in DB) AND members of a loaded
        // group are hidden from chip display — they're already covered by the
        // group chip. They stay in pendingUsers so their individual DB row is
        // preserved and survives a subsequent group removal.
        if (allGroupEmails.size > 0) {
          setHiddenByGroupEmails(
            new Set(
              emails
                .filter(e => allGroupEmails.has(e.toLowerCase()))
                .map(e => e.toLowerCase()),
            ),
          );
        }
      } else if (Array.isArray(data.allowedGroups)) {
        setLoadedGroupNames(data.allowedGroups);
        setPendingGroups(data.allowedGroups);
      }
    } catch (err: unknown) {
      setFetchError(
        err instanceof Error ? err.message : 'Failed to load access list',
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    accessBase,
    getAuthHeaders,
    annotationGroupNames,
    identityApi,
    backendUrl,
  ]);

  useEffect(() => {
    if (isOwner && currentUserEmail && !didInitialLoadRef.current) {
      didInitialLoadRef.current = true;
      loadAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, currentUserEmail]);

  // ── Search users (catalog internal + CIAM external) ────────────────────────

  const selectedEmailSet = useMemo(
    () => new Set(pendingUsers.map(e => e.toLowerCase())),
    [pendingUsers],
  );

  const searchUsers = useCallback(
    async (query: string) => {
      const q = query.trim();
      if (q.length < 2) {
        setUserOptions([]);
        return;
      }
      setUserSearching(true);
      try {
        const { token } = await identityApi.getCredentials();
        const authHeader: Record<string, string> = token
          ? { Authorization: `Bearer ${token}` }
          : {};

        // Search internal WellSky users via enterprise Okta (same endpoint as PublicAccessUserPicker).
        // Returns 503 in local dev if Okta private key not configured — caught silently.
        const oktaSearch = fetch(
          `${backendUrl}/api/microapps-api/access/users/search?q=${encodeURIComponent(
            q,
          )}`,
          { headers: authHeader, credentials: 'include' },
        )
          .then(async r => {
            if (!r.ok) return [] as UserOption[];
            const d = (await r.json()) as {
              users: Array<{
                profile: {
                  email: string;
                  firstName: string;
                  lastName: string;
                  displayName?: string;
                  login: string;
                };
              }>;
            };
            return (d.users ?? []).slice(0, 10).map(u => ({
              email: u.profile.email,
              displayName:
                u.profile.displayName ??
                (`${u.profile.firstName} ${u.profile.lastName}`.trim() ||
                  u.profile.login),
              source: 'internal' as const,
            }));
          })
          .catch(() => [] as UserOption[]);

        // Search external users from CIAM
        const ciamSearch = fetch(
          `${backendUrl}/api/microapps-api/ciam/users/search`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader },
            credentials: 'include',
            body: JSON.stringify({ search: q, pageSize: 10 }),
          },
        )
          .then(async r => {
            if (!r.ok) return [] as UserOption[];
            const d = (await r.json()) as {
              items: Array<{
                email: string;
                displayName: string;
              }>;
            };
            return (d.items ?? [])
              .filter(u => !u.email.toLowerCase().endsWith('@wellsky.com'))
              .map(u => ({
                email: u.email,
                displayName: u.displayName,
                source: 'external' as const,
              }));
          })
          .catch(() => [] as UserOption[]);

        const [internal, external] = await Promise.all([
          oktaSearch,
          ciamSearch,
        ]);

        // Deduplicate: Okta result wins; remove already-selected
        const seenEmails = new Set(internal.map(u => u.email.toLowerCase()));
        const deduped = external.filter(
          u => !seenEmails.has(u.email.toLowerCase()),
        );

        setUserOptions(
          [...internal, ...deduped].filter(
            u => !selectedEmailSet.has(u.email.toLowerCase()),
          ),
        );
      } catch {
        setUserOptions([]);
      } finally {
        setUserSearching(false);
      }
    },
    [backendUrl, identityApi, selectedEmailSet],
  );

  useEffect(() => {
    if (userTimerRef.current) clearTimeout(userTimerRef.current);
    userTimerRef.current = setTimeout(() => {
      void searchUsers(userInput);
    }, 300);
    return () => {
      if (userTimerRef.current) clearTimeout(userTimerRef.current);
    };
  }, [userInput, searchUsers]);

  // ── Search CIAM client groups ──────────────────────────────────────────────

  const selectedGroupSet = useMemo(
    () => new Set(pendingGroups.map(g => g.toLowerCase())),
    [pendingGroups],
  );

  const searchGroups = useCallback(
    async (query: string) => {
      if (query.trim().length < 2) {
        setGroupOptions([]);
        return;
      }
      setGroupSearching(true);
      try {
        const { token } = await identityApi.getCredentials();
        const res = await fetch(
          `${backendUrl}/api/microapps-api/ciam/groups/search`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
            body: JSON.stringify({ search: query.trim(), pageSize: 20 }),
          },
        );
        if (!res.ok) throw new Error(`Group search failed: ${res.status}`);
        const data = (await res.json()) as { items: CiamGroup[] };
        setGroupOptions(
          (data.items ?? []).filter(
            g => !selectedGroupSet.has(g.name.toLowerCase()),
          ),
        );
      } catch {
        setGroupOptions([]);
      } finally {
        setGroupSearching(false);
      }
    },
    [backendUrl, identityApi, selectedGroupSet],
  );

  useEffect(() => {
    if (groupTimerRef.current) clearTimeout(groupTimerRef.current);
    groupTimerRef.current = setTimeout(() => {
      void searchGroups(groupInput);
    }, 300);
    return () => {
      if (groupTimerRef.current) clearTimeout(groupTimerRef.current);
    };
  }, [groupInput, searchGroups]);

  // ── Add/remove handlers ────────────────────────────────────────────────────

  const handleAddUser = useCallback(
    (_: React.SyntheticEvent, opt: UserOption | string | null) => {
      if (!opt) return;
      const email = typeof opt === 'string' ? opt.trim() : opt.email;
      if (!EMAIL_RE.test(email)) return;
      if (selectedEmailSet.has(email.toLowerCase())) return;
      // The invite row's "Add …" label is not a display name.
      if (typeof opt !== 'string' && !isInviteOption(opt)) {
        setDisplayNames(prev => {
          const next = new Map(prev);
          next.set(opt.email.toLowerCase(), opt.displayName);
          return next;
        });
      }
      setPendingUsers(prev => [...prev, email]);
      setUserInput('');
      setUserOptions([]);
      setSuccessMsg(null);
    },
    [selectedEmailSet],
  );

  const handleRemoveUser = useCallback(
    (email: string) => {
      if (ownerEmails.includes(email.toLowerCase())) return;
      setPendingUsers(prev => prev.filter(e => e !== email));
      setSuccessMsg(null);
    },
    [ownerEmails],
  );

  const handleAddGroup = useCallback(
    (_: React.SyntheticEvent, group: CiamGroup | null) => {
      if (!group) return;
      setPendingGroups(prev => [...prev, group.name]);
      setGroupInput('');
      setGroupOptions([]);
      setSuccessMsg(null);

      // Fetch the group's members so we can immediately remove any that appear
      // as individual user chips — group access is implicit and should not be
      // duplicated in the Individual Users section.
      const removeGroupMembersFromUsers = async () => {
        try {
          const { token } = await identityApi.getCredentials();
          const res = await fetch(
            `${backendUrl}/api/microapps-api/ciam/groups/${group.id}/members`,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              credentials: 'include',
            },
          );
          if (!res.ok) return;
          const data = (await res.json()) as { emails: string[] };
          const members = data.emails ?? [];
          setGroupMembersCache(prev => {
            const next = new Map(prev);
            next.set(group.name, members);
            return next;
          });
          // Members who are already individually added must stay in pendingUsers
          // so their DB row is preserved (individual grant survives group removal).
          // They are only hidden from chip display via hiddenByGroupEmails.
          const memberSet = new Set(members.map(e => e.toLowerCase()));
          // Members who are also individually added in pendingUsers stay in
          // pendingUsers (individual DB row preserved) but are hidden from display.
          const currentPending = new Set(
            pendingUsers.map(e => e.toLowerCase()),
          );
          const toHide = members.filter(e =>
            currentPending.has(e.toLowerCase()),
          );
          if (toHide.length > 0) {
            setHiddenByGroupEmails(prev => {
              const next = new Set(prev);
              toHide.forEach(e => next.add(e.toLowerCase()));
              return next;
            });
          }
          // Only remove members who are NOT individually added (group-only access)
          setPendingUsers(prev =>
            prev.filter(
              e =>
                !memberSet.has(e.toLowerCase()) ||
                currentPending.has(e.toLowerCase()),
            ),
          );
        } catch {
          // Non-blocking — group is added regardless; members just stay visible
          // until the next save filters them via individualUsers.
        }
      };
      void removeGroupMembersFromUsers();
    },
    [backendUrl, identityApi, pendingUsers],
  );

  const handleRemoveGroup = useCallback(
    (name: string) => {
      const cachedMembers = groupMembersCache.get(name) ?? [];
      const memberSet = new Set(cachedMembers.map(e => e.toLowerCase()));

      // Build the cache without this group first so we can recompute coverage.
      const newCache = new Map(groupMembersCache);
      newCache.delete(name);

      // Build the set of emails still covered by remaining groups.
      const remainingGroupEmails = new Set<string>();
      for (const [, members] of newCache) {
        members.forEach(e => remainingGroupEmails.add(e.toLowerCase()));
      }

      // Un-hide individually-added members of the removed group that are no
      // longer covered by any other group. They should become visible chips.
      setHiddenByGroupEmails(prev => {
        const next = new Set(prev);
        memberSet.forEach(e => {
          if (!remainingGroupEmails.has(e)) {
            next.delete(e);
          }
        });
        return next;
      });

      // Remove group-only access from pendingUsers (members that are NOT
      // individually added — i.e. not in hiddenByGroupEmails).
      // Members in hiddenByGroupEmails stay in pendingUsers (individual grant).
      setPendingUsers(prev =>
        prev.filter(e => {
          const lower = e.toLowerCase();
          return !memberSet.has(lower) || hiddenByGroupEmails.has(lower);
        }),
      );

      setPendingGroups(prev => prev.filter(g => g !== name));
      setGroupMembersCache(newCache);
      setSuccessMsg(null);
    },
    [groupMembersCache, hiddenByGroupEmails],
  );

  const handleReset = useCallback(() => {
    setPendingUsers(loadedUsers);
    setPendingGroups(loadedGroupNames);
    // Keep groupMembersCache — it was pre-populated in loadAccess for all loaded
    // groups. Clearing it would break handleRemoveGroup for persisted groups.
    // Instead, recompute hiddenByGroupEmails from the loaded state.
    const allLoadedGroupEmails = new Set<string>();
    for (const g of loadedGroupNames) {
      const members = groupMembersCache.get(g) ?? [];
      members.forEach(e => allLoadedGroupEmails.add(e.toLowerCase()));
    }
    setHiddenByGroupEmails(
      new Set(
        loadedUsers
          .filter(e => allLoadedGroupEmails.has(e.toLowerCase()))
          .map(e => e.toLowerCase()),
      ),
    );
    setUserInput('');
    setGroupInput('');
    setUserOptions([]);
    setGroupOptions([]);
    setFetchError(null);
    setSuccessMsg(null);
  }, [loadedUsers, loadedGroupNames, groupMembersCache]);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setIsSaving(true);
    setFetchError(null);
    setSuccessMsg(null);
    try {
      const headers = await getAuthHeaders();
      // Compute delta — send only what changed, not the full list.
      const loadedLower = new Set(loadedUsers.map(e => e.toLowerCase()));
      const pendingLower = new Set(pendingUsers.map(e => e.toLowerCase()));
      const usersToAdd = pendingUsers.filter(
        e => !loadedLower.has(e.toLowerCase()),
      );
      const usersToRemove = loadedUsers.filter(
        e => !pendingLower.has(e.toLowerCase()),
      );
      // Group delta: added groups grant their members access immediately;
      // removed groups revoke members who have no other route to access.
      const loadedGroupSet = new Set(loadedGroupNames);
      const pendingGroupSet = new Set(pendingGroups);
      const groupsToAdd = pendingGroups.filter(g => !loadedGroupSet.has(g));
      const groupsToRemove = loadedGroupNames.filter(
        g => !pendingGroupSet.has(g),
      );

      const res = await fetch(`${accessBase}/access`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          usersToAdd,
          usersToRemove,
          groups: pendingGroups,
          groupsToAdd,
          groupsToRemove,
          ownerEmails,
          // Send the current individual users explicitly so the backend stores
          // exactly what the owner intended — avoids the CIAM-subtract heuristic
          // that can fail when a client group cannot be resolved at save time.
          individualUsers: pendingUsers,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const bodyError = (body as { error?: unknown }).error;
        throw new Error(
          typeof bodyError === 'string'
            ? bodyError
            : `Failed to update access (${res.status})`,
        );
      }
      const data = (await res.json()) as {
        users: string[];
        individualUsers?: string[];
        allowedGroups?: string[];
        gcpSyncWarning?: string;
      };
      // Use individualUsers so group-derived members are never shown as individual
      // user chips. Fall back to users for older backend versions.
      let saved: string[];
      if (Array.isArray(data.individualUsers)) {
        saved = data.individualUsers;
      } else if (Array.isArray(data.users)) {
        saved = data.users;
      } else {
        saved = pendingUsers;
      }
      setLoadedUsers(saved);
      setPendingUsers(saved);
      if (data.gcpSyncWarning) {
        setFetchError(data.gcpSyncWarning);
      }

      // Persist group names to the argo repo backstage.yaml annotation so they
      // survive catalog refreshes. Only needed when client groups actually changed —
      // individual user changes don't require touching the argo-repo file.
      const groupsChanged = groupsToAdd.length > 0 || groupsToRemove.length > 0;
      if (groupsChanged) {
        try {
          const groupsRes = await fetch(
            `${backendUrl}/api/microapps-api/update-public-app-groups`,
            {
              method: 'PUT',
              headers,
              credentials: 'include',
              body: JSON.stringify({
                appName,
                allowedGroupNames: pendingGroups,
              }),
            },
          );
          if (!groupsRes.ok) {
            const body = await groupsRes.json().catch(() => ({}));
            const bodyError = (body as { error?: unknown }).error;
            throw new Error(
              typeof bodyError === 'string'
                ? bodyError
                : `Failed to persist groups (${groupsRes.status})`,
            );
          }
        } catch (groupErr: unknown) {
          // Log but don't block — CIAM is already updated
          // eslint-disable-next-line no-console
          console.warn(
            'PublicAccessTab: failed to persist group names to argo repo',
            groupErr,
          );
        }
        // Prefer allowedGroups from the PUT response (set from in-process cache)
        // so the saved state is correct even before the argo-repo annotation re-indexes.
        setLoadedGroupNames(
          Array.isArray(data.allowedGroups)
            ? data.allowedGroups
            : pendingGroups,
        );
      } // end if (groupsChanged)

      setSuccessMsg('Access updated successfully.');
    } catch (err: unknown) {
      setFetchError(
        err instanceof Error ? err.message : 'Failed to update access',
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render: loading state ──────────────────────────────────────────────────

  if (!currentUserEmail) {
    return (
      <Box p={3} display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading…</Typography>
      </Box>
    );
  }

  if (!isOwner) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Only the app owner can manage access. Contact{' '}
          {ownerEmails.join(', ') || 'the app owner'} to request changes.
        </Alert>
      </Box>
    );
  }

  // ── Render: management UI ─────────────────────────────────────────────────

  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Manage Access
      </Typography>

      {!ciamGroupId && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>CIAM provisioning failed during deployment.</strong> The
          WellSky Identity access group for <em>{appName}</em> was not created,
          so <strong>no one can currently access this app</strong> (including
          you). Add the required users below and click{' '}
          <strong>Save Changes</strong> — this will create the CIAM group and
          restore access.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Control who can access <strong>{appName}</strong> on the public
        internet. Add/remove individual users or client groups, then click{' '}
        <strong>Save Changes</strong>. External users will receive an activation
        email if they're new to WellSky Identity.
      </Alert>

      {fetchError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setFetchError(null)}
        >
          {fetchError}
        </Alert>
      )}
      {successMsg && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMsg(null)}
        >
          {successMsg}
        </Alert>
      )}

      {/* ── Individual users ── */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" gutterBottom>
        Individual Users
      </Typography>

      {isLoading && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading CIAM group members…</Typography>
        </Box>
      )}

      {!isLoading && (
        <>
          {/* Filter out emails that are already covered by a client group — they
              stay in pendingUsers for DB persistence but should not show as
              individual chips (the group chip represents them). */}
          {(() => {
            const visibleUsers = pendingUsers.filter(
              e => !hiddenByGroupEmails.has(e.toLowerCase()),
            );
            return visibleUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No individual users added yet.
              </Typography>
            ) : (
              <Box
                display="flex"
                flexWrap="wrap"
                gap={1}
                mb={2}
                p={1.5}
                border={1}
                borderColor="divider"
                borderRadius={1}
              >
                {visibleUsers.map(email => {
                  const isOwnerEmail = ownerEmails.includes(
                    email.toLowerCase(),
                  );
                  const isNew = !loadedUsers.includes(email);
                  const isInternal = email
                    .toLowerCase()
                    .endsWith('@wellsky.com');
                  const displayName = displayNames.get(email.toLowerCase());
                  const chipLabel = displayName
                    ? `${displayName} (${email})${
                        isOwnerEmail ? ' · Owner' : ''
                      }`
                    : `${email}${isOwnerEmail ? ' · Owner' : ''}`;
                  return (
                    <Chip
                      key={email}
                      label={chipLabel}
                      size="small"
                      icon={
                        isInternal ? (
                          <BusinessIcon fontSize="small" />
                        ) : (
                          <PersonIcon fontSize="small" />
                        )
                      }
                      sx={
                        isInternal
                          ? {
                              ...INTERNAL_CHIP_SX,
                              ...(isNew ? { fontStyle: 'italic' } : {}),
                            }
                          : {
                              ...EXTERNAL_CHIP_SX,
                              ...(isNew ? { fontStyle: 'italic' } : {}),
                            }
                      }
                      onDelete={
                        isOwnerEmail ? undefined : () => handleRemoveUser(email)
                      }
                      title={getUserChipTitle(isOwnerEmail, isNew, isInternal)}
                    />
                  );
                })}
              </Box>
            );
          })()}

          <Autocomplete<UserOption, false, false, true>
            freeSolo
            options={userOptions}
            groupBy={opt => {
              if (typeof opt === 'string') return '';
              if (isInviteOption(opt)) return INVITE_GROUP;
              return opt.source === 'internal'
                ? INTERNAL_GROUP
                : EXTERNAL_GROUP;
            }}
            getOptionLabel={opt =>
              typeof opt === 'string'
                ? opt
                : `${opt.displayName} (${opt.email})`
            }
            isOptionEqualToValue={(o, v) =>
              typeof o !== 'string' &&
              typeof v !== 'string' &&
              o.email.toLowerCase() === v.email.toLowerCase()
            }
            inputValue={userInput}
            onInputChange={(_, val, reason) => {
              if (reason !== 'reset') setUserInput(val);
            }}
            onChange={handleAddUser}
            value={null}
            loading={userSearching}
            filterOptions={(opts, { inputValue }) => {
              const trimmed = inputValue.trim();
              if (
                EMAIL_RE.test(trimmed) &&
                !selectedEmailSet.has(trimmed.toLowerCase()) &&
                !opts.some(
                  (o): o is UserOption =>
                    typeof o !== 'string' &&
                    o.email.toLowerCase() === trimmed.toLowerCase(),
                )
              ) {
                return [
                  ...opts,
                  {
                    email: trimmed,
                    displayName: `Add "${trimmed}"`,
                    source: 'external' as const,
                  },
                ];
              }
              return opts;
            }}
            noOptionsText={
              userInput.trim().length < 2
                ? 'Type at least 2 characters to search'
                : 'No users found'
            }
            loadingText="Searching users…"
            className={pickerClasses.input}
            slotProps={{ paper: { className: pickerClasses.paper } }}
            renderInput={params => (
              <TextField
                {...params}
                label="Add user by name or email"
                placeholder="e.g. john or john@wellsky.com"
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {userSearching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderGroup={params => (
              <AccessPickerGroupHeader
                key={params.key}
                classes={pickerClasses}
                label={params.group}
                count={
                  params.group === INVITE_GROUP
                    ? 0
                    : React.Children.count(params.children)
                }
              >
                {params.children}
              </AccessPickerGroupHeader>
            )}
            renderOption={(props, opt) => {
              const { key: _key, ...liProps } =
                props as React.HTMLAttributes<HTMLLIElement> & {
                  key?: string;
                };
              const invite = isInviteOption(opt);
              let kind: AccessKind = 'external';
              if (invite) kind = 'invite';
              else if (opt.source === 'internal') kind = 'internal';
              return (
                <AccessPickerOption
                  key={opt.email}
                  liProps={liProps}
                  classes={pickerClasses}
                  kind={kind}
                  primary={invite ? `Invite ${opt.email}` : opt.displayName}
                  secondary={
                    invite
                      ? 'Sends an activation email if they are new to WellSky Identity'
                      : opt.email
                  }
                />
              );
            }}
          />
        </>
      )}

      {/* ── Client groups ── */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" gutterBottom>
        Client Groups
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        All members of a selected group will be granted access.
      </Typography>

      {pendingGroups.length > 0 && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={1}
          mb={2}
          p={1.5}
          border={1}
          borderColor="divider"
          borderRadius={1}
        >
          {pendingGroups.map(name => {
            const isNew = !loadedGroupNames.includes(name);
            return (
              <Chip
                key={name}
                label={name}
                size="small"
                icon={<GroupsIcon fontSize="small" />}
                sx={{
                  ...GROUP_CHIP_SX,
                  ...(isNew ? { fontStyle: 'italic' } : {}),
                }}
                onDelete={() => handleRemoveGroup(name)}
                title={isNew ? 'Pending addition' : undefined}
              />
            );
          })}
        </Box>
      )}

      <Autocomplete
        options={groupOptions}
        getOptionLabel={opt => opt.name}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        inputValue={groupInput}
        onInputChange={(_, val, reason) => {
          if (reason !== 'reset') setGroupInput(val);
        }}
        onChange={handleAddGroup}
        value={null}
        loading={groupSearching}
        filterOptions={x => x}
        noOptionsText={
          groupInput.trim().length < 2
            ? 'Type at least 2 characters to search'
            : 'No client groups found'
        }
        loadingText="Searching client groups…"
        className={pickerClasses.input}
        slotProps={{ paper: { className: pickerClasses.paper } }}
        renderInput={params => (
          <TextField
            {...params}
            label="Add client group"
            placeholder="Search by group name…"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {groupSearching && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, opt) => {
          const { key: _key, ...liProps } =
            props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
          return (
            <AccessPickerOption
              key={opt.id}
              liProps={liProps}
              classes={pickerClasses}
              kind="group"
              primary={opt.name}
              secondary={opt.description || undefined}
            />
          );
        }}
      />

      {/* ── Submit / Reset ── */}
      <Divider sx={{ my: 2 }} />
      <Box display="flex" gap={2} alignItems="center">
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!hasChanges || isSaving}
          startIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isSaving ? 'Saving…' : 'Save Changes'}
        </Button>
        <Button
          variant="text"
          onClick={handleReset}
          disabled={!hasChanges || isSaving}
        >
          Reset
        </Button>
        {hasChanges && !isSaving && (
          <Typography variant="caption" color="text.secondary">
            Unsaved changes
          </Typography>
        )}
      </Box>
    </Box>
  );
};
